/**
 * Cursor rule:
 * 1. Only `showSearch` enabled
 * 2. Only `open` is `true`
 * 3. When typing, set `open` to `true` which hit rule of 2
 *
 * Accessibility:
 * - https://www.w3.org/TR/wai-aria-practices/examples/combobox/aria1.1pattern/listbox-combo.html
 */
import * as React from 'react';
import { useRef } from 'react';
import KeyCode from 'rc-util/lib/KeyCode';
import MultipleSelector from './MultipleSelector';
import SingleSelector from './SingleSelector';
import useLock from '../hooks/useLock';
const Selector = (props, ref) => {
    const inputRef = useRef(null);
    const compositionStatusRef = useRef(false);
    const { prefixCls, multiple, open, mode, showSearch, tokenWithEnter, onSearch, onSearchSubmit, onToggleOpen, onInputKeyDown, domRef } = props;
    // ======================= Ref =======================
    React.useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current.focus();
        },
        blur: () => {
            inputRef.current.blur();
        }
    }));
    // ====================== Input ======================
    const [getInputMouseDown, setInputMouseDown] = useLock(0);
    const onInternalInputKeyDown = event => {
        const { which } = event;
        if (which === KeyCode.UP || which === KeyCode.DOWN) {
            event.preventDefault();
        }
        if (onInputKeyDown) {
            onInputKeyDown(event);
        }
        if (which === KeyCode.ENTER && mode === 'tags' && !compositionStatusRef.current && !open) {
            // When menu isn't open, OptionList won't trigger a value change
            // So when enter is pressed, the tag's input value should be emitted here to let selector know
            onSearchSubmit(event.target.value);
        }
        if (![KeyCode.SHIFT, KeyCode.TAB, KeyCode.BACKSPACE, KeyCode.ESC].includes(which)) {
            onToggleOpen(true);
        }
    };
    /**
   * We can not use `findDOMNode` sine it will get warning,
   * have to use timer to check if is input element.
   */
    const onInternalInputMouseDown = () => {
        setInputMouseDown(true);
    };
    // When paste come, ignore next onChange
    const pastedTextRef = useRef(null);
    const triggerOnSearch = (value) => {
        if (onSearch(value, true, compositionStatusRef.current) !== false) {
            onToggleOpen(true);
        }
    };
    const onInputCompositionStart = () => {
        compositionStatusRef.current = true;
    };
    const onInputCompositionEnd = e => {
        compositionStatusRef.current = false;
        triggerOnSearch(e.target.value);
    };
    const onInputChange = event => {
        let { target: { value } } = event;
        // Pasted text should replace back to origin content
        if (tokenWithEnter && pastedTextRef.current && /[\r\n]/.test(pastedTextRef.current)) {
            // CRLF will be treated as a single space for input element
            const replacedText = pastedTextRef.current
                .replace(/[\r\n]+$/, '')
                .replace(/\r\n/g, ' ')
                .replace(/[\r\n]/g, ' ');
            value = value.replace(replacedText, pastedTextRef.current);
        }
        pastedTextRef.current = null;
        triggerOnSearch(value);
    };
    const onInputPaste = e => {
        const { clipboardData } = e;
        const value = clipboardData.getData('text');
        pastedTextRef.current = value;
    };
    const onClick = ({ target }) => {
        if (target !== inputRef.current) {
            // Should focus input if click the selector
            const isIE = document.body.style.msTouchAction !== undefined;
            if (isIE) {
                setTimeout(() => {
                    inputRef.current.focus();
                });
            }
            else {
                inputRef.current.focus();
            }
        }
    };
    const onMouseDown = event => {
        const inputMouseDown = getInputMouseDown();
        if (event.target !== inputRef.current && !inputMouseDown) {
            event.preventDefault();
        }
        if ((mode !== 'combobox' && (!showSearch || !inputMouseDown)) || !open) {
            if (open) {
                onSearch('', true, false);
            }
            onToggleOpen();
        }
    };
    // ================= Inner Selector ==================
    const sharedProps = {
        inputRef,
        onInputKeyDown: onInternalInputKeyDown,
        onInputMouseDown: onInternalInputMouseDown,
        onInputChange,
        onInputPaste,
        onInputCompositionStart,
        onInputCompositionEnd
    };
    const selectNode = multiple ? (<MultipleSelector {...props} {...sharedProps}/>) : (<SingleSelector {...props} {...sharedProps}/>);
    return (<div ref={domRef} className={`${prefixCls}-selector`} onClick={onClick} onMouseDown={onMouseDown}>
            {selectNode}
        </div>);
};
const ForwardSelector = React.forwardRef(Selector);
ForwardSelector.displayName = 'Selector';
export default ForwardSelector;
